import React, { useMemo, useState } from "react";
import { CalendarCheck, Plus, CalendarDays, List, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

const INITIAL_TASKS = [
  { id: "MS-001", asset: "Hydrant Pump P-01",        loc: "Basement B1",    type: "Quarterly PM",       date: "2026-05-05", assignee: "Suresh Patil", status: "Upcoming",  last: "2026-02-12", system: "Fire" },
  { id: "MS-002", asset: "Sprinkler Valve Zone 3",   loc: "Level 2",        type: "Monthly Inspection", date: "2026-05-01", assignee: "Ravi Nair",    status: "Overdue",   last: "2026-04-01", system: "Fire" },
  { id: "MS-003", asset: "AHU-04 Filter Clean",      loc: "Food Court",     type: "Monthly",            date: "2026-05-03", assignee: "Anil Sharma",  status: "Overdue",   last: "2026-04-03", system: "HVAC" },
  { id: "MS-004", asset: "Fire Alarm Panel",         loc: "Security Room",  type: "Quarterly Test",     date: "2026-05-10", assignee: "Vaibhav P.",   status: "Scheduled", last: "2026-02-10", system: "Fire" },
  { id: "MS-005", asset: "DG Set Fuel Check",        loc: "Basement B2",    type: "Weekly",             date: "2026-05-04", assignee: "Suresh Patil", status: "Upcoming",  last: "2026-04-27", system: "Electrical" },
  { id: "MS-006", asset: "Lift Annual Service",      loc: "All Lifts",      type: "Annual",             date: "2026-05-15", assignee: "Otis AMC",     status: "Scheduled", last: "2025-05-15", system: "Lifts" },
  { id: "MS-007", asset: "Water Tank Cleaning",      loc: "OHT Level 5",    type: "Half-Yearly",        date: "2026-04-20", assignee: "Anil Sharma",  status: "Completed", last: "2026-04-20", system: "Plumbing" },
  { id: "MS-008", asset: "CCTV DVR Check",           loc: "Security Room",  type: "Monthly",            date: "2026-04-28", assignee: "Ravi Nair",    status: "Completed", last: "2026-04-28", system: "General" },
];

const STATUS_TONE = {
  Scheduled: "bg-emerald-500/15 text-emerald-300",
  Upcoming:  "bg-amber-500/15  text-amber-300",
  Overdue:   "bg-red-500/15    text-red-300",
  Completed: "bg-slate-500/15  text-slate-300",
};

const SYSTEMS = ["Fire", "HVAC", "Electrical", "Plumbing", "Lifts", "General"];
const FREQS = ["One-time", "Weekly", "Monthly", "Quarterly", "Half-Yearly", "Annual"];

function Kpi({ label, value, tone = "blue" }) {
  const c = {
    blue: "text-sky-300 bg-sky-500/10 border-sky-500/20",
    red: "text-red-300 bg-red-500/10 border-red-500/20",
    green: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20",
    amber: "text-amber-300 bg-amber-500/10 border-amber-500/20",
  }[tone];
  return (
    <div className={`glass rounded-lg p-5 border ${c}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-80 font-bold">{label}</div>
      <div className="text-4xl font-extrabold mt-2 tabular-nums">{value}</div>
    </div>
  );
}

export default function MaintenanceScheduler() {
  const [tasks, setTasks] = useState(INITIAL_TASKS);
  const [siteFilter, setSiteFilter] = useState("all");
  const [sysFilter, setSysFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [view, setView] = useState("list"); // list | calendar
  const [showPanel, setShowPanel] = useState(false);
  const [form, setForm] = useState({
    asset: "", loc: "", system: "Fire", type: "",
    frequency: "Monthly", date: "", assignee: "", notes: "",
  });

  const filtered = useMemo(() => tasks.filter((t) =>
    (sysFilter === "all" || t.system === sysFilter) &&
    (statusFilter === "all" || t.status === statusFilter)
  ), [tasks, sysFilter, statusFilter]);

  const kpis = useMemo(() => ({
    scheduled: tasks.filter((t) => t.date.startsWith("2026-05")).length,
    completed: tasks.filter((t) => t.status === "Completed").length,
    overdue: tasks.filter((t) => t.status === "Overdue").length,
    upcoming: tasks.filter((t) => t.status === "Upcoming").length,
  }), [tasks]);

  const save = () => {
    if (!form.asset || !form.date || !form.assignee) return toast.error("Asset, Date and Assignee are required");
    const id = `MS-${String(tasks.length + 1).padStart(3, "0")}`;
    setTasks([{ id, ...form, status: "Scheduled", last: "—" }, ...tasks]);
    setShowPanel(false);
    setForm({ asset: "", loc: "", system: "Fire", type: "", frequency: "Monthly", date: "", assignee: "", notes: "" });
    toast.success(`Task ${id} scheduled`);
  };

  // calendar grid — May 2026
  const calendarGrid = useMemo(() => {
    const days = [];
    for (let d = 1; d <= 31; d++) {
      const dateStr = `2026-05-${String(d).padStart(2, "0")}`;
      const hits = filtered.filter((t) => t.date === dateStr);
      days.push({ d, dateStr, hits });
    }
    return days;
  }, [filtered]);

  return (
    <div className="space-y-6" data-testid="maintenance-scheduler-page">
      {/* HEADER */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-2">
            <CalendarCheck className="w-8 h-8 text-emerald-400" strokeWidth={2.5} /> Maintenance Scheduler
          </h1>
          <p className="text-sm text-gray-500 mt-1">Preventive & scheduled maintenance across all systems</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="border-white/15" data-testid="view-toggle-btn"
                  onClick={() => setView(view === "list" ? "calendar" : "list")}>
            {view === "list" ? <><CalendarDays className="w-4 h-4 mr-1.5" /> Calendar</> : <><List className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> List</>}
          </Button>
          <Button className="f360-gradient border-0" data-testid="add-task-btn" onClick={() => setShowPanel(true)}>
            <Plus className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Add Maintenance Task
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Scheduled This Month" value={kpis.scheduled} tone="blue" />
        <Kpi label="Completed" value={kpis.completed} tone="green" />
        <Kpi label="Overdue" value={kpis.overdue} tone="red" />
        <Kpi label="Upcoming (7 days)" value={kpis.upcoming} tone="amber" />
      </div>

      {/* FILTERS */}
      <div className="glass rounded-lg p-4 flex items-center gap-3 flex-wrap">
        <span className="text-[11px] uppercase tracking-widest text-gray-500">Filters</span>
        <Select value={siteFilter} onValueChange={setSiteFilter}>
          <SelectTrigger className="w-48 bg-gray-50 border-gray-200"><SelectValue placeholder="Site" /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900">
            <SelectItem value="all">All Sites</SelectItem>
            <SelectItem value="phoenix">Phoenix Marketcity Mall</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sysFilter} onValueChange={setSysFilter}>
          <SelectTrigger className="w-40 bg-gray-50 border-gray-200"><SelectValue placeholder="System" /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900">
            <SelectItem value="all">All Systems</SelectItem>
            {SYSTEMS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-gray-50 border-gray-200"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900">
            <SelectItem value="all">All Status</SelectItem>
            {Object.keys(STATUS_TONE).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="month" defaultValue="2026-05" className="w-40 bg-gray-50 border-gray-200" />
      </div>

      {view === "list" ? (
        <div className="glass rounded-lg overflow-hidden" data-testid="maintenance-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50">
                <tr>
                  <th className="text-left px-5 py-3">Task ID</th>
                  <th className="text-left py-3">Asset / System</th>
                  <th className="text-left py-3">Location</th>
                  <th className="text-left py-3">Type</th>
                  <th className="text-left py-3">Scheduled</th>
                  <th className="text-left py-3">Assigned To</th>
                  <th className="text-center py-3">Status</th>
                  <th className="text-left py-3">Last Done</th>
                  <th className="text-right px-5 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} className="border-t border-gray-200 hover:bg-gray-50" data-testid={`task-row-${t.id}`}>
                    <td className="px-5 py-3 font-mono text-xs">{t.id}</td>
                    <td className="py-3 font-semibold">{t.asset}</td>
                    <td className="py-3 text-gray-500">{t.loc}</td>
                    <td className="py-3">{t.type}</td>
                    <td className="py-3 font-mono text-xs">{new Date(t.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                    <td className="py-3">{t.assignee}</td>
                    <td className="py-3 text-center">
                      <span className={`inline-block px-2.5 py-1 rounded-full ${STATUS_TONE[t.status]} text-[10px] uppercase tracking-widest font-bold`}>{t.status}</span>
                    </td>
                    <td className="py-3 text-[11px] text-gray-500">{t.last === "—" ? t.last : new Date(t.last).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                    <td className="px-5 py-3 text-right">
                      <button className="text-orange-300 hover:text-gray-900 text-xs" data-testid={`view-task-${t.id}`} onClick={() => toast.info(`Viewing ${t.id}`)}>View →</button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={9} className="text-center text-gray-500 py-10">No tasks match your filters.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="glass rounded-lg p-5" data-testid="maintenance-calendar">
          <h3 className="font-semibold mb-3">May 2026</h3>
          <div className="grid grid-cols-7 gap-2 text-center">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d} className="text-[10px] uppercase tracking-wider text-gray-500 font-bold py-1">{d}</div>)}
            {/* May 2026 starts on Friday (offset 5) */}
            {Array.from({ length: 5 }).map((_, i) => <div key={`pad-${i}`} />)}
            {calendarGrid.map((c) => (
              <div key={c.d} className={`min-h-[68px] bg-gray-50 rounded-lg p-1.5 text-left hover:bg-gray-100 transition ${c.hits.length ? "border border-orange-500/30" : ""}`}>
                <div className="text-[10px] font-bold text-gray-600">{c.d}</div>
                <div className="mt-1 space-y-0.5">
                  {c.hits.slice(0, 2).map((h) => (
                    <div key={h.id} className={`text-[9px] px-1 py-0.5 rounded truncate ${STATUS_TONE[h.status]}`}>{h.asset.split(" ").slice(0, 2).join(" ")}</div>
                  ))}
                  {c.hits.length > 2 && <div className="text-[9px] text-gray-500">+{c.hits.length - 2} more</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SLIDE-IN PANEL */}
      {showPanel && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setShowPanel(false)}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative ml-auto w-full max-w-md h-full bg-white border-l border-gray-200 overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="add-task-panel">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-xl font-bold">Add Maintenance Task</h2>
              <button onClick={() => setShowPanel(false)} className="text-gray-500 hover:text-gray-900"><X className="w-5 h-5" strokeWidth={2.5} /></button>
            </div>
            <div className="p-5 space-y-4">
              <label className="flex flex-col gap-1 text-sm"><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Asset / System</span>
                <Input value={form.asset} onChange={(e) => setForm({ ...form, asset: e.target.value })} className="bg-gray-50 border-gray-200" placeholder="e.g. Hydrant Pump P-01" data-testid="form-asset" /></label>
              <label className="flex flex-col gap-1 text-sm"><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Location</span>
                <Input value={form.loc} onChange={(e) => setForm({ ...form, loc: e.target.value })} className="bg-gray-50 border-gray-200" placeholder="e.g. Basement B1" /></label>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1 text-sm"><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">System</span>
                  <Select value={form.system} onValueChange={(v) => setForm({ ...form, system: v })}>
                    <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 text-gray-900">{SYSTEMS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select></label>
                <label className="flex flex-col gap-1 text-sm"><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Frequency</span>
                  <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                    <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 text-gray-900">{FREQS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select></label>
              </div>
              <label className="flex flex-col gap-1 text-sm"><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Maintenance Type</span>
                <Input value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="bg-gray-50 border-gray-200" placeholder="e.g. Quarterly PM" /></label>
              <label className="flex flex-col gap-1 text-sm"><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Scheduled Date</span>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-gray-50 border-gray-200" data-testid="form-date" /></label>
              <label className="flex flex-col gap-1 text-sm"><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Assigned To</span>
                <Input value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })} className="bg-gray-50 border-gray-200" placeholder="e.g. Suresh Patil" data-testid="form-assignee" /></label>
              <label className="flex flex-col gap-1 text-sm"><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Notes</span>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-gray-50 border-gray-200" rows={3} /></label>
              <div className="pt-2 flex gap-2">
                <Button className="flex-1 f360-gradient border-0" onClick={save} data-testid="save-task-btn">Save Task</Button>
                <Button variant="outline" className="border-white/15" onClick={() => setShowPanel(false)}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
